/**
 * ServiceProcess - Reusable process steps section for service pages
 * Displays alternating image/text layout for process steps with scroll animations
 */

import { useEffect, useRef, useState } from 'react';
import { ProcessStep } from '../../types';

interface ServiceProcessProps {
  title: string;
  subtitle: string;
  steps: ProcessStep[];
}

interface ProcessStepItemProps {
  step: ProcessStep;
  index: number;
}

function ProcessStepItem({ step, index }: ProcessStepItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          if (ref.current) observer.unobserve(ref.current);
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (observer && ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center"
    >
      {/* Content - alternates position on desktop only */}
      <div
        className={`${isEven ? 'lg:order-2' : 'lg:order-1'} transition-all duration-1000 ${
          isVisible
            ? 'opacity-100 translate-x-0'
            : isEven
            ? 'opacity-0 -translate-x-10'
            : 'opacity-0 translate-x-10'
        }`}
      >
        <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sky-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-lg sm:text-xl">
              {step.step}
            </span>
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
              {step.title}
            </h3>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
              {step.description}
            </p>
          </div>
        </div>
      </div>

      {/* Image - alternates position on desktop only */}
      <div
        className={`${isEven ? 'lg:order-1' : 'lg:order-2'} transition-all duration-1000 delay-200 ${
          isVisible
            ? 'opacity-100 translate-x-0'
            : isEven
            ? 'opacity-0 translate-x-10'
            : 'opacity-0 -translate-x-10'
        }`}
      >
        <div className="w-full h-56 sm:h-72 md:h-80 lg:h-96 rounded-xl overflow-hidden shadow-xl">
          <img
            src={step.image}
            alt={step.title}
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </div>
  );
}

export default function ServiceProcess({
  title,
  subtitle,
  steps,
}: ServiceProcessProps) {
  return (
    <section className="py-12 sm:py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            {title}
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-2">
            {subtitle}
          </p>
        </div>

        {/* Process Steps */}
        <div className="space-y-12 sm:space-y-16 md:space-y-24">
          {steps.map((step, index) => (
            <ProcessStepItem key={index} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
